import { Briefcase, HeartHandshake, Rocket, type LucideIcon } from "lucide-react";

/**
 * One colour per track, everywhere the track record draws it: tiles, badges
 * and the activity chart. The same brand hues as the roles (chats/roleStyle):
 * paid work is the worker's action blue, volunteering the people pink,
 * creating the sun. Full class strings, not interpolated, so Tailwind's
 * scanner keeps them.
 */
export type Track = "paid" | "volunteer" | "creator" | "other";

/**
 * Small labels on a tinted tile, so the darkest readable shade of each hue:
 * brand-800 and human-800 on light; `cta-ink` is already the readable sun and
 * flips to the sun itself in dark mode.
 */
export const TRACK_TEXT: Record<Track, string> = {
  paid: "text-brand-800 dark:text-brand-700",
  volunteer: "text-human-800 dark:text-human-300",
  creator: "text-cta-ink",
  other: "text-brand-600 dark:text-brand-400",
};

export const TRACK_BG: Record<Track, string> = {
  paid: "bg-brand-500",
  volunteer: "bg-human-500",
  creator: "bg-cta",
  other: "bg-brand-400",
};

/** Badge pills, keyed by badge kind (`worker` is the paid-work track). */
export const BADGE_PILL: Record<"worker" | "volunteer" | "creator", string> = {
  worker: "bg-brand-100 text-brand-800 dark:bg-brand-500/20 dark:text-brand-700",
  volunteer: "bg-human-100 text-human-700 dark:bg-human-500/20 dark:text-human-300",
  creator: "bg-cta-light text-ink dark:bg-cta/20 dark:text-cta-ink",
};

/** Soft track tints: the activity chart's count chips. */
export const TRACK_TINT: Record<Track, string> = {
  paid: "bg-brand-500/10",
  volunteer: "bg-human-500/10",
  creator: "bg-cta/15",
  other: "bg-brand-400/10",
};

/** The icon each scored track wears on its tile and in the menu strip. */
export const TRACK_ICON: Record<Exclude<Track, "other">, LucideIcon> = {
  paid: Briefcase,
  volunteer: HeartHandshake,
  creator: Rocket,
};
